import React from 'react';
import {
  Box,
  GDSPosition,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  useClickableCard,
  UtilityText,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import {
  BadgeList,
  BadgeListProps,
} from '#common/badge-list/badge-list.component.tsx';
import { SquareImageContainer } from '#common/square-image-container/square-image-container.component.tsx';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { ConditionalLinkCardRenderer } from '../common/conditional-link-card-renderer/conditional-link-card-renderer.component';
import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import {
  MasonryDeeplinkAction,
  MasonryDriverAction,
  MasonryDismissAction,
} from '../../types/masonry-driver-node-actions';

export type MasonrySmallCardNodeProperties = {
  image?: string;
  eyebrow?: string;
  heading?: string;
  body?: string;
  imageAltText?: string;
  imageAlignment?: GDSPosition;
  badges?: BadgeListProps['badges'];
  isChildItem?: boolean;
  badgeBackgroundColor?: string;
};

type MasonrySmallCardNodeActions = {
  onClick?: MasonryDriverAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonrySmallCardNode = MasonryEngineNode<
  'smallCard',
  MasonrySmallCardNodeProperties,
  MasonrySmallCardNodeActions
>;

export type MasonrySmallCardRendererProps =
  MasonryEngineNodeRendererProps<MasonrySmallCardNode>;

const IMAGE_MAX_WIDTH = '98px';
const IMAGE_MAX_DESKTOP_WIDTH = '112px';

export const MasonrySmallCardRenderer = ({
  eyebrow,
  heading,
  body,
  image,
  imageAlignment = 'leading',
  badges,
  imageAltText = '',
  isChildItem = false,
  badgeBackgroundColor,
  onClick,
  onDismiss,
  className,
  engineClassName,
  locatorId,
  css,
  rootElementRef,
}: MasonrySmallCardRendererProps) => {
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);

  const invokeAction = useInvokeAction();
  const overlineId = `svc-${heading?.replace(/\s+/g, '-')}`;

  const onClickDeeplinkAction = onClick as MasonryDeeplinkAction;

  return (
    <ConditionalLinkCardRenderer
      hasLink={Boolean(onClickDeeplinkAction?.payload.url)}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      onDismiss={onDismiss}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        horizontalAlignment="spaceBetween"
        spacing="$threeQuarters"
        css={{
          padding: '$one',
        }}
        data-testid={locatorId}
      >
        {image && imageAlignment === 'leading' && (
          <StackItem>
            <SquareImageContainer
              src={image}
              alt={imageAltText}
              imageLoading="lazy"
              maxWidth={IMAGE_MAX_WIDTH}
              maxWidthDesktop={
                isChildItem ? IMAGE_MAX_WIDTH : IMAGE_MAX_DESKTOP_WIDTH
              }
            />
          </StackItem>
        )}
        <StackItem verticalAlignment="center" css={{ flex: '2' }}>
          <StackItem>
            {eyebrow && (
              <UtilityText id={overlineId} size="eyebrow" as="p">
                {eyebrow}
              </UtilityText>
            )}
            <StackLayout css={{ marginTop: '$quarter' }}>
              {heading && (
                <HeadingText
                  level={!body ? 'display' : '3'}
                  size="sm"
                  aria-describedby={overlineId}
                >
                  <ConditionalLinkRenderer
                    disableDefaultOnClick
                    ref={primaryActionRef}
                    url={onClickDeeplinkAction?.payload.url}
                    onClick={(event: React.MouseEvent) => {
                      if (onClick) {
                        invokeAction({
                          event,
                          ...onClick,
                        });
                      }
                    }}
                  >
                    {heading}
                  </ConditionalLinkRenderer>
                </HeadingText>
              )}
              {body && (
                <ParagraphText
                  size="sm"
                  htmlStringOptions={BodyTextHtmlTagOptions}
                >
                  {body}
                </ParagraphText>
              )}
            </StackLayout>
          </StackItem>
          {badges && (
            <Box css={{ marginTop: '$threeQuarters' }}>
              <BadgeList
                badges={badges}
                backgroundColor={badgeBackgroundColor}
              />
            </Box>
          )}
        </StackItem>
        {image && imageAlignment === 'trailing' && (
          <StackItem>
            <SquareImageContainer
              src={image}
              alt={imageAltText}
              imageLoading="lazy"
              maxWidth={IMAGE_MAX_WIDTH}
              maxWidthDesktop={
                isChildItem ? IMAGE_MAX_WIDTH : IMAGE_MAX_DESKTOP_WIDTH
              }
            />
          </StackItem>
        )}
      </StackLayout>
    </ConditionalLinkCardRenderer>
  );
};
